// ---------------------------------------------------------------------------
// brendaMapping — data-shape mappers
// ---------------------------------------------------------------------------
//
// Turns raw Brenda charges fragments into the lean shapes consumed by
// components (`BrendaChargesData`, `BrendaChargesExtra`,
// `BrendaChargesPickupPoint`, etc). Kept separate from the charges
// hook's composition code (`brendaCharges.ts`) and the stock / pricing
// rule files (`brendaStock.ts`, `brendaPrices.ts`) so each concern
// has one home.
// ---------------------------------------------------------------------------

// App imports
import {
  getBrendaDeliveryPrice,
  getBrendaProductPrice,
  getBrendaProductVat,
} from '~/utils/brendaPrices'
import { resolveBrendaDeliveryStock } from '~/utils/brendaStock'

// Types
import type {
  ExtrasItem,
  GetPricesHireData,
  ItemTotals,
  LinkedProductStock,
  PickupPoint as RawPickupPoint,
} from '~/types'
import type {
  BrendaChargesData,
  BrendaChargesExtra,
  BrendaChargesLinkedStock,
  BrendaChargesPickupPoint,
  BrendaChargesSubmit,
} from '~/types/brendaCharges'

// ---------------------------------------------------------------------------
// Product-type inference
// ---------------------------------------------------------------------------

/**
 * Brenda doesn't tag the main item with a product type — we infer it
 * from which totals it returned. Hire (`bulk`) products have
 * `hireTotal` populated; consumables have the resale totals.
 * `hireTotal` (the base value) is a more reliable signal than
 * `hireTotalIncVat` because the derived field can be omitted even
 * when the product is clearly a hire.
 */
export const inferBrendaProductType = (
  charges: GetPricesHireData
): 'bulk' | 'consumable' => {
  return charges.item?.itemTotals?.hireTotal?.amount != null
    ? 'bulk'
    : 'consumable'
}

// ---------------------------------------------------------------------------
// Linked product stock
// ---------------------------------------------------------------------------

/**
 * Strips the raw per-branch `LinkedProductStock[]` down to the
 * `{ stockCode, maxQuantity }` subset the app actually consumes —
 * dropping `productId` which no caller reads.
 */
export const mapBrendaLinkedStock = (
  linked: LinkedProductStock[] | undefined | null
): BrendaChargesLinkedStock[] =>
  (linked ?? []).map(item => ({
    stockCode: item.stockCode,
    maxQuantity: item.maxQuantity,
  }))

// ---------------------------------------------------------------------------
// Pickup points (display list)
// ---------------------------------------------------------------------------

/**
 * Maps a raw `PickupPoint` into the display shape — stripped of fields
 * components don't render and with the linked-stock list narrowed.
 */
export const mapBrendaPickupPoint = (
  raw: RawPickupPoint
): BrendaChargesPickupPoint => ({
  id: raw.id,
  name: raw.name,
  branchCode: raw.branchCode,
  locationName: raw.locationName,
  customerSupplierName: raw.customerSupplierName,
  siteLocation: raw.siteLocation,
  distance: raw.distance,
  quantity: raw.quantity ?? 0,
  firstAvailableDate: raw.firstAvailableDate ?? null,
  linked: mapBrendaLinkedStock(raw.linkedProductAvailability),
})

// ---------------------------------------------------------------------------
// Damage waiver — inc-VAT computed fee
// ---------------------------------------------------------------------------

/**
 * Computes the inc-VAT damage waiver fee from itemTotals
 * (damageWaiver + damageWaiverVat). Returns 0 when either field is
 * missing (e.g. consumable products that don't carry DW).
 */
const getDamageWaiverFee = (totals: ItemTotals): number => {
  const dw = totals.damageWaiver?.amount ?? 0
  const dwVat = totals.damageWaiverVat?.amount ?? 0
  return Math.round((dw + dwVat) * 100) / 100
}

// ---------------------------------------------------------------------------
// Add-ons / cross-sells
// ---------------------------------------------------------------------------

/**
 * Maps a raw `ExtrasItem` (linked or cross-sell) into the display
 * shape, resolving its delivery availability via the same rules as the
 * parent product. Both hire and buy products flow through this — they
 * just originate from different arrays on the charges response
 * (`linkedHireProducts` / `linkedResaleProducts` / `crossSell*`).
 *
 * Delivery rules mirror `resolveBrendaDeliveryStock` — see that
 * function's JSDoc for the full priority order (`firstAvailableDate`
 * first, then `siteWithStock`, with a sensible fallback when
 * `siteWithStock` is absent).
 *
 * Pickup availability for linked products is per-branch, driven by the
 * parent branch's `linkedProductAvailability`, so it lives on the
 * `BrendaChargesPickup` summary rather than on each extra.
 *
 * Linked add-ons must also be deliverable within the parent's window:
 * if the extra's resolved date lands **after** the parent's resolved
 * delivery date, we flag it as unavailable so it can't be added. This
 * covers both the "in stock now but can't ship in time" path and the
 * "future-dated stock" path. Cross-sells ship independently so the
 * gate doesn't apply to them.
 */
export const mapBrendaExtra = (
  extra: ExtrasItem,
  productType: 'bulk' | 'consumable',
  parentResolvedDate: string | null,
  requestedDate: string | null,
  /** When true, skip the future-date gate — cross-sells ship independently. */
  isCrossSell = false
): BrendaChargesExtra | null => {
  const price = getBrendaProductPrice(extra.itemTotals, productType)
  if (!price) return null

  const vat = getBrendaProductVat(extra.itemTotals, productType) ?? {
    currency: price.currency,
    amount: 0,
  }

  // Distinguish "siteWithStock missing" (null) from "siteWithStock
  // present with value 0" (number). `resolveBrendaDeliveryStock`
  // treats them differently — missing falls through to the fallback
  // path, zero falls through to out-of-stock.
  const siteMax = extra.siteWithStock?.maxQuantity
  const deliveryStock = typeof siteMax === 'number' ? siteMax : null

  const stock = resolveBrendaDeliveryStock(
    productType,
    deliveryStock,
    extra.deliveryDate ?? null,
    extra.firstAvailableDate ?? null,
    requestedDate
  )

  // Compare the extra's resolved availability date to the parent's
  // resolved delivery date (post mandatory-linked bump). Any date
  // past the parent's means the extra can't land in time — treat as
  // unavailable. Applies equally to "in stock now" and "future stock"
  // paths since Brenda always returns a `deliveryDate`.
  const laterThanParent =
    !isCrossSell &&
    stock.available &&
    !!stock.date &&
    !!parentResolvedDate &&
    stock.date > parentResolvedDate
  const available = stock.available && !laterThanParent

  const deliveryPrice = getBrendaDeliveryPrice(extra.itemTotals)

  return {
    stockCode: String(extra.product?.stockCode ?? ''),
    title: extra.product?.name ?? '',
    image: extra.product?.image ?? { imageUrl: '' },
    price,
    vat,
    productType,
    isMandatory: Boolean(extra.isMandatory),
    // Inc-VAT damage waiver fee computed from itemTotals
    // (damageWaiver + damageWaiverVat). Written to basket meta as
    // `hire_damage_waiver` so Lunar stores the actual £ value.
    damageWaiver: getDamageWaiverFee(extra.itemTotals),
    // Prefer the explicit `deposit` field when Brenda returns one;
    // otherwise derive it from the totals gap. Both the GET (single-item)
    // and POST (multi-item) responses include totalIncludingDeposit and
    // grossTotal on linked itemTotals, so the fallback always works.
    deposit: extra.deposit ?? null,
    // Raw passthrough so consumers like the pickup branch viability
    // check can read the future-stock date independently of the
    // delivery-side resolution. Null when the API didn't return one.
    firstAvailableDate: extra.firstAvailableDate ?? null,
    delivery: {
      available,
      date: available ? stock.date : null,
      price: available ? (deliveryPrice ?? null) : null,
      maxQuantity: available ? stock.maxQuantity : 0,
    },
  }
}

// ---------------------------------------------------------------------------
// Full product-data envelope
// ---------------------------------------------------------------------------

/**
 * Builds the lean "display data" view of a charges response — the bits
 * components need to render a product card / add-to-basket control.
 * Parent dates/stock live on the wrapping result's `delivery` / `pickup`
 * fields; each linked / cross-sell item carries its own resolved
 * `delivery` summary on its own entry.
 */
export const mapBrendaChargesData = (
  charges: GetPricesHireData,
  request: BrendaChargesSubmit,
  /**
   * Parent's resolved delivery date (post mandatory-linked bump).
   * Used as the comparator for gating linked add-ons — any extra
   * whose resolved date lands later than this is flagged unavailable.
   * Falls back to `request.startDate` when no resolution is available
   * yet (e.g. unavailable responses).
   */
  parentResolvedDate: string | null = null
): BrendaChargesData => {
  const mainItem = charges.item
  const productType = inferBrendaProductType(charges)
  const gateDate = parentResolvedDate ?? request.startDate
  const requestedDate = request.startDate

  const addOns = [
    ...(charges.linkedResaleProducts ?? []).map(e =>
      mapBrendaExtra(e, 'consumable', gateDate, requestedDate)
    ),
    ...(charges.linkedHireProducts ?? []).map(e =>
      mapBrendaExtra(e, 'bulk', gateDate, requestedDate)
    ),
  ].filter((extra): extra is BrendaChargesExtra => extra !== null)

  const crossSells = [
    ...(charges.crossSellConsumableProducts ?? []).map(e =>
      mapBrendaExtra(e, 'consumable', gateDate, requestedDate, true)
    ),
    ...(charges.crossSellHireProducts ?? []).map(e =>
      mapBrendaExtra(e, 'bulk', gateDate, requestedDate, true)
    ),
  ].filter((extra): extra is BrendaChargesExtra => extra !== null)

  return {
    groupId: crypto.randomUUID(),
    stockCode: String(mainItem?.product?.stockCode ?? ''),
    deposit: mainItem?.deposit ?? null,
    // Inc-VAT damage waiver fee computed from the main item's
    // itemTotals (damageWaiver + damageWaiverVat).
    damageWaiver: mainItem?.itemTotals
      ? getDamageWaiverFee(mainItem.itemTotals)
      : 0,
    price: getBrendaProductPrice(mainItem?.itemTotals, productType),
    requiresFraudCheck: Boolean(mainItem?.requiresFraudCheck),
    pickupPoints: (charges.pickupPoints ?? []).map(mapBrendaPickupPoint),
    addOns,
    crossSells,
  }
}
